import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import Input from '../../Input';
import PrimaryCta from '../../PrimaryCta';

type Props = {
  open: boolean;
  onClose: () => void;
  onCreate: (p: { name: string; color: string; code?: string }) => Promise<void> | void;
  nameOnly?: boolean;
  title?: string;
  submitLabel?: string;
  initialName?: string;
};

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 60;
  display: grid;
  place-items: center;
  background: rgba(0, 0, 0, .55);
`;

const Card = styled.div`
  position: relative;
  width: 100%;
  max-width: 420px;
  padding: 22px;
  border-radius: 16px;
  color: #E8EAED;
  background: linear-gradient(180deg, rgba(20, 22, 28, .96), rgba(18, 20, 25, .96));
  border: 1px solid rgba(255, 255, 255, .06);
  box-shadow: 0 18px 48px rgba(0, 0, 0, .45);
  display: grid;
  gap: 12px;
`;

const CloseX = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, .08);
  background: rgba(255, 255, 255, .04);
  color: #cbd2e1;
  display: grid;
  place-items: center;
  font-size: 18px;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, .08);
  }
`;

const Title = styled.h3`
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 800;
`;

const Label = styled.label`
  display: block;
  margin: 8px 2px 6px;
  color: #cbd2e1;
  font-size: 12px;
  font-weight: 800;
`;

const SwatchGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(8, 28px);
  gap: 10px;
  margin-top: 4px;
`;

const Swatch = styled.button<{ $c: string; $sel: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  cursor: pointer;
  background: ${({ $c }) => $c};
  border: ${({ $sel }) => ($sel ? '2px solid #E8EAED' : '1px solid rgba(255, 255, 255, .12)')};
  box-shadow: ${({ $sel }) => ($sel ? '0 0 0 2px rgba(199, 210, 254, .45)' : 'none')};
`;

const palette = [
  '#FCA5A5', '#F87171', '#FB7185', '#F59E0B', '#FCD34D', '#A7F3D0', '#34D399',
  '#93C5FD', '#60A5FA', '#C4B5FD', '#E9D5FF', '#FBCFE8', '#FDBA74', '#7DD3FC',
  '#6EE7B7', '#DDD6FE',
];

function codeFromName(name: string) {
  const parts = name.trim().split(/\s+/);
  if (!parts.length) return '';
  const abbr = parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : parts[0].slice(0, 2);
  return abbr.toUpperCase();
}

export default function CreateProjectModal({
  open,
  onClose,
  onCreate,
  nameOnly = false,
  title = 'Create project',
  submitLabel = 'Create project',
  initialName = '',
}: Props) {
  const [name, setName] = useState('');
  const [color, setColor] = useState(palette[0]);
  const code = useMemo(() => codeFromName(name), [name]);

  useEffect(() => {
    if (!open) return;
    setName(initialName);
  }, [initialName, open]);

  useEffect(() => {
    if (!open) return;
    const onEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [open, onClose]);

  if (!open) return null;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return;
    await onCreate({ name: name.trim(), color, code });
    setName('');
  };

  return createPortal(
    <Backdrop onClick={onClose}>
      <Card role="dialog" aria-modal="true" aria-label={title} onClick={(event) => event.stopPropagation()}>
        <CloseX onClick={onClose} aria-label="Close">x</CloseX>
        <Title>{title}</Title>

        <form onSubmit={submit} style={{ display: 'grid', gap: 10 }}>
          <div>
            <Label>Project name</Label>
            <Input value={name} onChange={(event) => setName(event.target.value)} placeholder="e.g. Website Refresh" required />
          </div>

          {!nameOnly ? (
            <>
              <div>
                <Label>Color</Label>
                <SwatchGrid>
                  {palette.map((swatch) => (
                    <Swatch
                      key={swatch}
                      $c={swatch}
                      $sel={swatch === color}
                      onClick={(event) => {
                        event.preventDefault();
                        setColor(swatch);
                      }}
                      aria-label={`Color ${swatch}`}
                    />
                  ))}
                </SwatchGrid>
              </div>

              <div style={{ color: '#9AA4B2', fontSize: 12 }}>
                Badge preview:
                <span
                  style={{
                    display: 'inline-grid',
                    placeItems: 'center',
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: color,
                    color: '#111827',
                    fontWeight: 900,
                    marginLeft: 8,
                  }}
                >
                  {code || 'o'}
                </span>
              </div>
            </>
          ) : null}

          <PrimaryCta type="submit" disabled={!name.trim()}>
            {submitLabel}
          </PrimaryCta>
        </form>
      </Card>
    </Backdrop>,
    document.body,
  );
}
